import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, Select, Checkbox, Button, Spin, Alert, Space, Typography } from 'antd';
import axios from 'axios';
import { API_DOMAIN } from '../constants';
import { fetchChatFilters } from '../qv4/helpers';
import useSnackbar from '../Snackbar/UseSnackbar';

const CHAT_SETTINGS_MODE = "chat_settings";

const FORMAT_LABELS = { bold: "Bold", italic: "Italic", strikethrough: "Strikethrough", monospace: "Monospace" };
const DEFAULT_FORMATS = { bold: true, italic: true, strikethrough: false, monospace: false };
const DEFAULT_CHAT_SETTINGS = {
  chat_filters: { allowed_filters: [], default_filter: null, include_admins: false },
  outbound_signature: { enabled: false, applicable_user_ids: null, formats: DEFAULT_FORMATS, template: "" },
};

// Outer -> inner wrap order: Italic -> Bold -> Strikethrough -> Monospace,
// fixed regardless of which order the checkboxes were toggled.
function wrapFormatted(text, formats) {
  let s = text;
  if (formats.monospace) s = "```" + s + "```";
  if (formats.strikethrough) s = `~${s}~`;
  if (formats.bold) s = `*${s}*`;
  if (formats.italic) s = `_${s}_`;
  return s;
}

function DefaultModeTab({ authState, filterOptions, chatFilters, setChatFilters }) {
  const filterNames = filterOptions.map(f => f.name);
  const { allowed_filters: allowedFilters, default_filter: defaultFilter, include_admins: includeAdmins } = chatFilters;
  const defaultOptions = (allowedFilters.length > 0 ? allowedFilters : filterNames);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Allowed chat filters</div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
          Which filters agents can choose from in the chat side panel. Leave empty to allow all.
        </div>
        <Select
          mode="multiple"
          style={{ width: "100%" }}
          placeholder="All filters allowed"
          value={allowedFilters}
          onChange={v => setChatFilters(prev => ({
            ...prev,
            allowed_filters: v,
            default_filter: (prev.default_filter && v.length > 0 && !v.includes(prev.default_filter)) ? null : prev.default_filter,
          }))}
          options={filterNames.map(name => ({ value: name, label: name }))}
        />
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Default chat filter</div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
          Which filter is selected by default when the chat panel opens.
        </div>
        <Select
          style={{ width: "100%" }}
          placeholder="WATI's own default"
          allowClear
          value={defaultFilter}
          onChange={v => setChatFilters(prev => ({ ...prev, default_filter: v }))}
          options={defaultOptions.map(name => ({ value: name, label: name }))}
        />
      </div>
      <div>
        <Checkbox
          checked={includeAdmins}
          onChange={e => setChatFilters(prev => ({ ...prev, include_admins: e.target.checked }))}
        >
          Include admins
        </Checkbox>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
          Admins are exempt from the allowed-filters and default-filter restrictions above by default — they always see every filter, unrestricted. Check this to apply the same restrictions to admin accounts too.
        </div>
      </div>
    </div>
  );
}

function OutboundMsgTab({ outboundSignature, setOutboundSignature, userOptions }) {
  const { enabled, formats, applicable_user_ids: applicableUserIds } = outboundSignature;
  const appliesToAllUsers = applicableUserIds === null;

  const setFormats = (updater) => setOutboundSignature(prev => ({ ...prev, formats: updater(prev.formats) }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Alert
        type="info"
        showIcon
        message="Changes here can take up to 30 seconds to apply to in-flight messages, since the backend caches this setting briefly instead of re-checking it on every single send."
      />
      <Checkbox checked={enabled} onChange={e => setOutboundSignature(prev => ({ ...prev, enabled: e.target.checked }))}>
        Add agent name in every sent message on top
      </Checkbox>

      <div style={{ opacity: enabled ? 1 : 0.45, pointerEvents: enabled ? "auto" : "none" }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Who receives the signature?</div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
          The signature is added only to messages sent by the users selected here. This applies to text messages and media captions.
        </div>
        <Checkbox
          checked={appliesToAllUsers}
          onChange={e => setOutboundSignature(prev => ({
            ...prev,
            applicable_user_ids: e.target.checked ? null : userOptions.map(option => option.value),
          }))}
        >
          Apply to all active users, including users added later
        </Checkbox>
        <div style={{ marginTop: 10, marginBottom: 18 }}>
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
            disabled={appliesToAllUsers || !enabled}
            style={{ width: "100%" }}
            placeholder={appliesToAllUsers ? "All active users" : "Select users"}
            value={Array.isArray(applicableUserIds) ? applicableUserIds : []}
            onChange={values => setOutboundSignature(prev => ({
              ...prev,
              applicable_user_ids: [...new Set(values.map(Number))],
            }))}
            options={userOptions}
          />
          {!appliesToAllUsers && (
            <Space size={12} style={{ marginTop: 6 }}>
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => setOutboundSignature(prev => ({
                  ...prev,
                  applicable_user_ids: userOptions.map(option => option.value),
                }))}
              >
                Select all
              </Button>
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() => setOutboundSignature(prev => ({ ...prev, applicable_user_ids: [] }))}
              >
                Clear selection
              </Button>
              {applicableUserIds.length === 0 && (
                <Typography.Text type="warning" style={{ fontSize: 12 }}>
                  The signature will not be added for anyone.
                </Typography.Text>
              )}
            </Space>
          )}
        </div>

        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Formatting</div>
        <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
          {Object.keys(FORMAT_LABELS).map(key => (
            <Checkbox
              key={key}
              disabled={!enabled}
              checked={formats[key]}
              onChange={e => setFormats(prev => ({ ...prev, [key]: e.target.checked }))}
            >
              {FORMAT_LABELS[key]}
            </Checkbox>
          ))}
        </div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>Preview</div>
        <div style={{
          border: "1px solid #d9d9d9", borderRadius: 6, padding: "4px 11px", fontSize: 14, minHeight: 32, display: "flex", alignItems: "center",
          fontWeight: formats.bold ? 700 : 400, fontStyle: formats.italic ? "italic" : "normal", textDecoration: formats.strikethrough ? "line-through" : "none", fontFamily: formats.monospace ? "monospace" : "inherit",
        }}>
          John Doe
        </div>
      </div>
    </div>
  );
}

export function ChatSettingsPanel({ authState }) {
  const head = useMemo(() => ({ headers: { accessToken: localStorage.getItem("accessToken") } }), []);
  const { SuccessSnackbar, ErrorSnackbar } = useSnackbar();

  const [activeKey, setActiveKey] = useState("default_mode");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterOptions, setFilterOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [chatFilters, setChatFilters] = useState(DEFAULT_CHAT_SETTINGS.chat_filters);
  const [outboundSignature, setOutboundSignature] = useState(DEFAULT_CHAT_SETTINGS.outbound_signature);

  useEffect(() => {
    if (!authState?.id) return;
    setLoading(true);
    Promise.all([
      fetchChatFilters(authState.id),
      axios.post(`${API_DOMAIN}/whatsapp/get_misc_mode`, { mode: CHAT_SETTINGS_MODE }, head),
      axios.get(`${API_DOMAIN}/auth/users/chat-settings-options`, head),
    ]).then(([filters, res, usersRes]) => {
      setFilterOptions(filters);
      const data = res.data?.success ? res.data.data : null;
      const users = usersRes.data?.success && Array.isArray(usersRes.data.data) ? usersRes.data.data : [];
      const options = users
        .map(user => {
          const id = Number(user.id);
          const fullName = [user.name, user.surname].filter(Boolean).join(" ").trim();
          return Number.isInteger(id) && id > 0
            ? { value: id, label: fullName || user.username || `User #${id}` }
            : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label));
      const savedApplicableUserIds = data?.outbound_signature?.applicable_user_ids;
      const applicableUserIds = Array.isArray(savedApplicableUserIds)
        ? [...new Set(savedApplicableUserIds.map(Number).filter(id => Number.isInteger(id) && id > 0))]
        : null;
      const knownOptionIds = new Set(options.map(option => option.value));
      (applicableUserIds || []).forEach(id => {
        if (!knownOptionIds.has(id)) options.push({ value: id, label: `User #${id}` });
      });
      setUserOptions(options);
      setChatFilters({
        allowed_filters: Array.isArray(data?.chat_filters?.allowed_filters) ? data.chat_filters.allowed_filters : [],
        default_filter: data?.chat_filters?.default_filter || null,
        include_admins: !!data?.chat_filters?.include_admins,
      });
      setOutboundSignature({
        enabled: !!data?.outbound_signature?.enabled,
        applicable_user_ids: applicableUserIds,
        formats: data?.outbound_signature?.formats ? { ...DEFAULT_FORMATS, ...data.outbound_signature.formats } : DEFAULT_FORMATS,
        template: data?.outbound_signature?.template || "",
      });
    }).catch(error => {
      ErrorSnackbar(error.response?.data?.message || error.message || "Unable to load chat settings.");
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState?.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_DOMAIN}/whatsapp/save_misc_mode`, {
        mode: CHAT_SETTINGS_MODE,
        moredetails: {
          chat_filters: chatFilters,
          outbound_signature: {
            enabled: outboundSignature.enabled,
            applicable_user_ids: outboundSignature.applicable_user_ids,
            formats: outboundSignature.formats,
            template: wrapFormatted("{{agent_name}}", outboundSignature.formats),
          },
        },
      }, head);
      if (res.data.success) SuccessSnackbar("Chat settings saved!");
      else ErrorSnackbar(res.data.message);
    } catch (error) {
      ErrorSnackbar(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><Spin /></div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Tabs
        activeKey={activeKey}
        onChange={setActiveKey}
        size="small"
        items={[
          { key: "default_mode", label: "Default Mode", children: <DefaultModeTab authState={authState} filterOptions={filterOptions} chatFilters={chatFilters} setChatFilters={setChatFilters} /> },
          { key: "outbound_msg", label: "Outbound Msg Settings", children: <OutboundMsgTab outboundSignature={outboundSignature} setOutboundSignature={setOutboundSignature} userOptions={userOptions} /> },
        ]}
      />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button type="primary" loading={saving} onClick={handleSave}>Save</Button>
      </div>
    </div>
  );
}
